import React, { useEffect, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { communityServicesAPI } from "../../../services/api";

/* 🔵 Shared Status Color Logic */
const getStatusStyle = (status) => {
  const s = status?.toLowerCase();
  switch (s) {
    case "active":
      return "bg-green-100 text-green-700";
    case "inactive":
      return "bg-red-100 text-red-700";
    case "pending":
      return "bg-yellow-100 text-yellow-700";
    default:
      return "bg-gray-100 text-gray-600";
  }
};

const VolunteerDetails = ({ isOpen, onClose, volunteer }) => {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && volunteer?.id) {
      fetchVolunteerDetails(volunteer.id);
    }
    if (!isOpen) setDetails(null); // reset when closed
  }, [isOpen, volunteer]);

  const fetchVolunteerDetails = async (id) => {
    try {
      setLoading(true);
      const res = await communityServicesAPI.getVolunteerById(id);
      setDetails(res.data.data.volunteer);
    } catch (err) {
      console.error("Failed to fetch volunteer details", err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const resident = details?.resident;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-6 font-sans">
      <div className="w-full max-w-4xl max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-y-auto animate-in fade-in zoom-in duration-200">
        
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-100">
          <h2 className="text-xl font-bold text-gray-800">Volunteer Details</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-7 h-7" />
          </button>
        </div>

        <div className="p-8">
          {loading ? (
            <p className="text-center text-gray-500">Loading volunteer details...</p>
          ) : details ? (
            <div className="bg-gray-50/50 rounded-2xl p-8 border border-gray-100">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-y-8 gap-x-6">

                {/* Row 1 */}
                <div>
                  <p className="text-sm text-gray-500 mb-1">Volunteer Type</p>
                  <p className="text-gray-700 font-medium">
                    {details.is_resident ? "Resident" : "Non-Resident"}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-500 mb-1">Name</p>
                  <p className="text-gray-700 font-medium">
                    {details.is_resident ? resident?.full_name : details.volunteer_name || "—"}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-500 mb-1">Contact Info</p>
                  <p className="text-gray-700 font-medium">
                    {details.is_resident ? resident?.contact_no : details.contact_information || "—"}
                  </p>
                </div>

                {/* Row 2 */}
                <div className="md:col-span-3 flex flex-wrap items-start gap-10">

                  <div className="min-w-[220px]">
                    <p className="text-sm text-gray-500 mb-1">Assigned Programs</p>
                    {details.programs?.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {details.programs.map((program) => (
                          <span
                            key={program.id}
                            className="text-gray-700 font-medium"
                          >
                            {program.program_name || program.name}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-400 italic">No programs assigned</p>
                    )}
                  </div>

                  <div className="min-w-[140px]">
                    <p className="text-sm text-gray-500 mb-1">Role</p>
                    <p className="text-gray-700 font-medium capitalize">{details.role}</p>
                  </div>

                  <div className="min-w-[140px]">
                    <p className="text-sm text-gray-500 mb-1">Status</p>
                    <span className={`inline-flex px-3 py-0.5 rounded-full text-sm font-medium ${getStatusStyle(details.status)}`}>
                      {details.status
                          ? details.status.charAt(0).toUpperCase() + details.status.slice(1).toLowerCase()
                          : "—"}
                    </span>
                  </div>

                </div>

                {/* Row 3 */}
                <div>
                  <p className="text-sm text-gray-500 mb-1">Date Created</p>
                  <p className="text-gray-700 font-medium">
                    {details.created_at ? new Date(details.created_at).toLocaleString() : "—"}
                  </p>
                </div>

                <div className="md:col-span-2">
                  <p className="text-sm text-gray-500 mb-1">Date Updated</p>
                  <p className="text-gray-700 font-medium">
                    {details.updated_at ? new Date(details.updated_at).toLocaleString() : "—"}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-center text-gray-500">No details found.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default VolunteerDetails;
